// ============================================
// CONTEXTO DE AUTENTICAÇÃO
// Gerencia login, logout e controle de acesso
// ============================================

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getApiUrl } from '../config/environment';
import { SystemRole, hasRoutePermission, getDefaultRoute, getRoleLabel } from '../config/permissions';

// ============================================
// TIPOS
// ============================================

export interface UnitContextSummary {
  id: string;
  name: string;
  short_name: string;
  is_matrix: boolean;
  can_manage: boolean;
  is_default: boolean;
}

export interface AuthUser {
  id: string;
  email: string;
  full_name: string;
  role: SystemRole;
  professional_id: string | null;
  avatar_url: string | null;
  phone: string | null;
  is_active: boolean;
  must_change_password: boolean;
  last_login_at: string | null;
  preferences: Record<string, any>;
  professional?: {
    id: string;
    full_name: string;
    specialty: string;
    registration_number: string;
    registration_type: string;
    avatar_url: string | null;
  };
  // Multi-unidade
  current_unit?: UnitContextSummary | null;
  available_units?: UnitContextSummary[];
}

interface AuthContextType {
  user: AuthUser | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  needsSetup: boolean;
  
  // Actions
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  setup: (data: { email: string; password: string; full_name: string; setup_key: string }) => Promise<void>;
  refreshUser: () => Promise<void>;
  switchUnit: (unitId: string) => Promise<void>;

  // Helpers
  isAdmin: boolean;
  isProfessional: boolean;
  hasPermission: (path: string) => boolean;
  roleLabel: string;
  defaultRoute: string;
  currentUnit: UnitContextSummary | null;
  availableUnits: UnitContextSummary[];
}

// ============================================
// CONTEXTO
// ============================================

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ============================================
// PROVIDER
// ============================================

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [needsSetup, setNeedsSetup] = useState(false);

  // ============================================
  // Inicialização - verificar token salvo
  // ============================================

  useEffect(() => {
    const initAuth = async () => {
      try {
        const savedToken = localStorage.getItem('auth_token');
        const savedUser = localStorage.getItem('auth_user');

        if (savedToken && savedUser) {
          // Verificar se o token ainda é válido
          try {
            const response = await axios.get(`${getApiUrl()}/auth/me`, {
              headers: { Authorization: `Bearer ${savedToken}` },
            });

            if (response.data.success && response.data.data) {
              setToken(savedToken);
              setUser(response.data.data);
              setNeedsSetup(false);
            } else {
              clearAuth();
              await checkSetup();
            }
          } catch (error) {
            // Token inválido ou expirado
            clearAuth();
            await checkSetup();
          }
        } else {
          // Sem token salvo - verificar se precisa de setup
          await checkSetup();
        }
      } catch (error) {
        console.error('Auth init error:', error);
        clearAuth();
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  // ============================================
  // Verificar se o setup é necessário
  // ============================================

  const checkSetup = async () => {
    try {
      const response = await axios.get(`${getApiUrl()}/auth/setup-status`);
      if (response.data.success) {
        setNeedsSetup(response.data.data.needs_setup);
      }
    } catch (error) {
      console.error('Setup check error:', error);
      // Se não conseguir verificar, assume que não precisa de setup
      setNeedsSetup(false);
    }
  };

  // ============================================
  // Login
  // ============================================

  const login = useCallback(async (email: string, password: string) => {
    try {
      const response = await axios.post(`${getApiUrl()}/auth/login`, {
        email,
        password,
      });

      if (response.data.success && response.data.data) {
        const { user: userData, token: authToken } = response.data.data;

        setToken(authToken);
        setUser(userData);
        setNeedsSetup(false);

        // Salvar no localStorage
        localStorage.setItem('auth_token', authToken);
        localStorage.setItem('auth_user', JSON.stringify(userData));

        // Compatibilidade com sistema antigo
        localStorage.setItem('token', authToken);
        localStorage.setItem('isAuthenticated', 'true');
        localStorage.setItem('user', JSON.stringify({
          id: userData.id,
          name: userData.full_name,
          email: userData.email,
        }));

        // Restaura "última unidade" salva da sessão anterior se diferente da default
        // e o usuário ainda tem acesso. Só atualiza last_unit_id depois de resolver.
        let resolvedUnitId: string | undefined = userData?.current_unit?.id;
        try {
          const lastUnitId = localStorage.getItem('last_unit_id');
          const availableIds: string[] = (userData.available_units || []).map((u: any) => u.id);
          if (
            lastUnitId &&
            availableIds.includes(lastUnitId) &&
            resolvedUnitId !== lastUnitId
          ) {
            const switchResp = await axios.post(
              `${getApiUrl()}/auth/switch-unit`,
              { unit_id: lastUnitId },
              { headers: { Authorization: `Bearer ${authToken}` } },
            );
            if (switchResp.data?.success && switchResp.data.data) {
              const { user: u2, token: t2 } = switchResp.data.data;
              setToken(t2);
              setUser(u2);
              localStorage.setItem('auth_token', t2);
              localStorage.setItem('auth_user', JSON.stringify(u2));
              localStorage.setItem('token', t2);
              resolvedUnitId = u2?.current_unit?.id || resolvedUnitId;
            }
          }
        } catch {
          // Falha ao restaurar última unidade não bloqueia o login
        }
        if (resolvedUnitId) {
          localStorage.setItem('last_unit_id', resolvedUnitId);
        }
      } else {
        throw new Error(response.data.error || 'Erro ao fazer login.');
      }
    } catch (error: any) {
      const message = error.response?.data?.error || error.message || 'Erro ao fazer login.';
      throw new Error(message);
    }
  }, []);

  // ============================================
  // Setup (criar primeiro admin)
  // ============================================

  const setup = useCallback(async (data: { email: string; password: string; full_name: string; setup_key: string }) => {
    try {
      const response = await axios.post(`${getApiUrl()}/auth/setup`, data);

      if (response.data.success && response.data.data) {
        const { user: userData, token: authToken } = response.data.data;

        setToken(authToken);
        setUser(userData);
        setNeedsSetup(false);

        localStorage.setItem('auth_token', authToken);
        localStorage.setItem('auth_user', JSON.stringify(userData));
        localStorage.setItem('token', authToken);
        localStorage.setItem('isAuthenticated', 'true');
        localStorage.setItem('user', JSON.stringify({
          id: userData.id,
          name: userData.full_name,
          email: userData.email,
        }));
      } else {
        throw new Error(response.data.error || 'Erro ao realizar setup.');
      }
    } catch (error: any) {
      const message = error.response?.data?.error || error.message || 'Erro ao realizar setup.';
      throw new Error(message);
    }
  }, []);

  // ============================================
  // Logout
  // ============================================

  const logout = useCallback(() => {
    // Chamar logout no backend (fire-and-forget)
    if (token) {
      axios.post(`${getApiUrl()}/auth/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {});
    }

    clearAuth();
  }, [token]);

  // ============================================
  // Refresh user data
  // ============================================

  const refreshUser = useCallback(async () => {
    if (!token) return;

    try {
      const response = await axios.get(`${getApiUrl()}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success && response.data.data) {
        setUser(response.data.data);
        localStorage.setItem('auth_user', JSON.stringify(response.data.data));
      }
    } catch (error) {
      console.error('Refresh user error:', error);
    }
  }, [token]);

  // ============================================
  // Switch unit (multi-unidade)
  // ============================================

  const switchUnit = useCallback(async (unitId: string) => {
    if (!token) throw new Error('Sessão expirada.');
    try {
      const response = await axios.post(
        `${getApiUrl()}/auth/switch-unit`,
        { unit_id: unitId },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data.success && response.data.data) {
        const { user: userData, token: authToken } = response.data.data;
        setToken(authToken);
        setUser(userData);
        localStorage.setItem('auth_token', authToken);
        localStorage.setItem('auth_user', JSON.stringify(userData));
        localStorage.setItem('token', authToken);
        if (userData?.current_unit?.id) {
          localStorage.setItem('last_unit_id', userData.current_unit.id);
        }
        // Notificar a aplicação para invalidar caches/refetchar
        window.dispatchEvent(new CustomEvent('unit:changed', { detail: { unitId: userData?.current_unit?.id } }));
      } else {
        throw new Error(response.data.error || 'Erro ao trocar unidade.');
      }
    } catch (error: any) {
      const message = error.response?.data?.error || error.message || 'Erro ao trocar unidade.';
      throw new Error(message);
    }
  }, [token]);

  // ============================================
  // Limpar autenticação
  // ============================================

  const clearAuth = () => {
    setToken(null);
    setUser(null);
    
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    
    // Limpar dados do sistema antigo
    localStorage.removeItem('token');
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('user');
    localStorage.removeItem('skip_auth');
  };

  // ============================================
  // Helpers computados
  // ============================================

  const isAuthenticated = !!token && !!user;
  const isAdmin = user?.role === 'admin' || user?.role === 'admin_master';
  const isProfessional = user?.role === 'professional';
  const roleLabel = user ? getRoleLabel(user.role) : '';
  const defaultRoute = user ? getDefaultRoute(user.role) : '/login';

  const hasPermission = useCallback((path: string): boolean => {
    if (!user) return false;
    return hasRoutePermission(user.role, path);
  }, [user]);

  // ============================================
  // Contexto
  // ============================================

  const currentUnit = user?.current_unit || null;
  const availableUnits = user?.available_units || [];

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated,
    isLoading,
    needsSetup,
    login,
    logout,
    setup,
    refreshUser,
    switchUnit,
    isAdmin,
    isProfessional,
    hasPermission,
    roleLabel,
    defaultRoute,
    currentUnit,
    availableUnits,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// ============================================
// HOOK
// ============================================

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
